import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import SharedModal from '../../../shared/_TBComponents/Modal';
import Button from '../../../shared/_TBComponents/Button';
import { formatWeekDateRangeCompact } from '../utils/weekUtils';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  font-family: 'Nunito Sans', sans-serif;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WeekNumber = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #9E9E9E;
  font-family: 'Nunito Sans', sans-serif;
  line-height: 1.5;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const WeekDateRange = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  line-height: normal;
  letter-spacing: -0.24px;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 180px;
  padding: 16px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  font-size: 16px;
  line-height: 1.5;
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 400;
  color: #444444;
  background: #FFFFFF;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border: 1px solid #444444;
  }

  &::placeholder {
    color: #9E9E9E;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
`;

/**
 * NotesModal Component
 *
 * Modal for adding/editing weekly notes in Review mode.
 * Displays a textarea for note content with save/cancel actions.
 * Shows the week number and date range in the header.
 */
const NotesModal = ({
  week, initialNote, onSave, onClose,
}) => {
  const [noteText, setNoteText] = useState(initialNote || '');
  const textareaRef = useRef(null);

  // Extract week number from ISO week string (e.g., '2025-W35' -> 35)
  const weekMatch = week.match(/^(\d{4})-W(\d{2})$/);
  const weekNumber = weekMatch ? parseInt(weekMatch[2], 10) : '';

  // Format the date range for display (e.g., 'Aug 29-Sep 4')
  const dateRange = formatWeekDateRangeCompact(week);

  // Auto-focus textarea on mount with a slight delay to ensure modal is rendered
  useEffect(() => {
    const timer = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Move cursor to the end of the text
        const { length } = textareaRef.current.value;
        textareaRef.current.setSelectionRange(length, length);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSave = () => {
    onSave(week, noteText);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSave();
    }
  };

  return (
    <SharedModal
      isOpen
      onRequestClose={onClose}
      hideTitle
      customButton
      hideButton
      customMinHeight='auto'
      customMaxWidth='600px'
      customPadding='24px'
      customBorderRadius='8px'
      overlayBackgroundColor='rgba(17, 24, 39, 0.55)'
    >
      <ModalContent>
        <ModalHeader>
          <HeaderTop>
            <HeaderText>
              <WeekNumber>
                WEEK
                {weekNumber}
              </WeekNumber>
              <WeekDateRange>{dateRange}</WeekDateRange>
            </HeaderText>
          </HeaderTop>
          <TextArea
            ref={textareaRef}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Add a note'
            aria-label='Planner notes textarea'
          />
        </ModalHeader>

        <ButtonGroup>
          <Button
            name='Save'
            onClick={handleSave}
            bgColor='#444444'
            txtColor='#FFFFFF'
            noBorder
            upperCase
            fontSize='16px'
            customBorderRadius='4px'
            styledWrapperProps={{
              flex: 1,
              height: '36px',
              padding: '6px 16px',
              fontWeight: 700,
              letterSpacing: '0.48px',
              lineHeight: 1.5,
            }}
            styledTextProps={{
              fontWeight: 700,
            }}
          />
          <Button
            name='Cancel'
            onClick={handleCancel}
            upperCase
            fontSize='16px'
            customBorderRadius='4px'
            styledWrapperProps={{
              flex: 1,
              height: '36px',
              padding: '6px 16px',
              background: 'transparent',
              border: '2px solid #444444',
              fontWeight: 700,
              letterSpacing: '0.48px',
              lineHeight: 1.5,
            }}
            styledTextProps={{
              fontWeight: 700,
            }}
          />
        </ButtonGroup>
      </ModalContent>
    </SharedModal>
  );
};

NotesModal.propTypes = {
  week: PropTypes.string.isRequired,
  initialNote: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

NotesModal.defaultProps = {
  initialNote: '',
};

export default NotesModal;
